import { capitalCase } from 'change-case';
import dayjs from 'dayjs';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from '~/utils/toast';
import { SkeletonActionButton } from '~/components/ActionButton';
import Button from '~/components/Button';
import Modal, { useModal } from '~/components/Modal';
import Table, { type Column } from '~/components/Table';
import { api, type RouterOutputs } from '~/utils/api';
import { serverError } from '~/utils/serverError';

import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { type ScannerAssociation } from '@prisma/client';

import NewJobButton, { Frequency } from './NewJobButton';
import { AppListingType, Routes, UNKNOWN_BRAND_ID } from '~/config';
import { ActorID } from '~/server/utils/apify/ActorID';
import { useRouter, useSearchParams } from 'next/navigation';
import Pagination, { usePagination } from '~/components/Pagination';
import { useWorkspace } from '~/contexts/WorkspaceContext';

type ScannerJob = RouterOutputs['scanner']['getAllScannerJobs']['scannerJobs'][number];
type JobsQueryInput = {
  isInfluencer: boolean;
  skip: number;
  limit: number;
};

export const fromScheduler = (
  scheduler: string | null,
): { frequency: Frequency; weekDay: number; monthDay: number } => {
  if (!scheduler) return { frequency: Frequency.ONCE, weekDay: 0, monthDay: 0 };

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const [_minute, _hour, monthDay, _month, weekDay] = scheduler.split(' ');

  if (!weekDay || !monthDay) {
    return { frequency: Frequency.ONCE, weekDay: 0, monthDay: 0 };
  }

  if (weekDay !== '*') {
    return { frequency: Frequency.WEEKLY, weekDay: parseInt(weekDay), monthDay: 0 };
  } else if (monthDay !== '*') {
    return { frequency: Frequency.MONTHLY, weekDay: 0, monthDay: parseInt(monthDay) };
  } else {
    return { frequency: Frequency.DAILY, weekDay: 0, monthDay: 0 };
  }
};

export const formatSchedulerLabel = (scheduler: string | null | undefined): string => {
  if (!scheduler) return 'One Time';
  try {
    const { frequency, monthDay, weekDay } = fromScheduler(scheduler);
    switch (frequency) {
      case Frequency.WEEKLY:
        return `Weekly on ${new Date(0, 0, weekDay).toLocaleString('en-US', { weekday: 'long' })}`;
      case Frequency.MONTHLY:
        return `Monthly on ${monthDay}`;
      case Frequency.DAILY:
        return 'Daily';
      default:
        return capitalCase(frequency);
    }
  } catch {
    return 'Recurring';
  }
};

const ScheduledJobActions = ({
  jobId,
  jobName,
  editHref,
  queryInput,
  isPendingDelete,
  onPendingDelete,
  onUndoDelete,
  onDeleteSettled,
}: {
  jobId: string;
  jobName: string | null;
  editHref: string;
  queryInput: JobsQueryInput;
  isPendingDelete: boolean;
  onPendingDelete: (jobId: string) => void;
  onUndoDelete: (jobId: string) => void;
  onDeleteSettled: (jobId: string) => void;
}) => {
  const router = useRouter();
  const { openModal, modalRef, closeModal } = useModal();
  const { mutate: deleteScannerJob, isLoading } = api.scanner.deleteScannerJob.useMutation();
  const utils = api.useUtils();
  const queryInputRef = useRef(queryInput);
  queryInputRef.current = queryInput;

  const onDelete = () => {
    closeModal();
    const queryKey = queryInput;
    const previous = utils.scanner.getAllScannerJobs.getData(queryKey);

    onPendingDelete(jobId);
    utils.scanner.getAllScannerJobs.setData(queryKey, (prev) =>
      prev
        ? {
            ...prev,
            scannerJobs: prev.scannerJobs.filter((job) => job.id !== jobId),
            totalCount: Math.max(0, prev.totalCount - 1),
          }
        : prev,
    );

    const restoreJobsCache = () => {
      if (!previous) return;
      utils.scanner.getAllScannerJobs.setData(queryKey, previous);
      const currentQueryKey = queryInputRef.current;
      if (JSON.stringify(currentQueryKey) === JSON.stringify(queryKey)) {
        return;
      }
      const restored = previous.scannerJobs.find((job) => job.id === jobId);
      if (!restored) return;
      utils.scanner.getAllScannerJobs.setData(currentQueryKey, (prev) => {
        if (!prev || prev.scannerJobs.some((job) => job.id === jobId)) {
          return prev;
        }
        return {
          ...prev,
          scannerJobs: [restored, ...prev.scannerJobs],
          totalCount: prev.totalCount + 1,
        };
      });
    };

    toast.undoable({
      message: 'Scheduled job deleted',
      description: jobName ?? undefined,
      onUndo: () => {
        onUndoDelete(jobId);
        restoreJobsCache();
      },
      onCommit: () => {
        deleteScannerJob(
          { id: jobId },
          {
            onError: (error) => {
              const message = error.message ?? '';
              if (/not found/i.test(message)) {
                onDeleteSettled(jobId);
                void utils.scanner.getAllScannerJobs.invalidate();
                return;
              }
              serverError(error);
              onUndoDelete(jobId);
              restoreJobsCache();
            },
            onSuccess: () => {
              onDeleteSettled(jobId);
              void utils.scanner.getAllScannerJobs.invalidate();
            },
          },
        );
      },
    });
  };

  return (
    <>
      <SkeletonActionButton
        disabled={isPendingDelete}
        options={[
          {
            icon: PencilIcon,
            label: 'Edit',
            onClick: () => {
              router.push(editHref);
            },
          },
          {
            icon: TrashIcon,
            label: 'Delete',
            isWarning: true,
            disabled: isPendingDelete,
            onClick: () => {
              openModal();
            },
          },
        ]}
      />
      <Modal
        ref={modalRef}
        header="Delete Scheduled Job"
        subHeader={
          <>
            Are you sure you want to delete this scanner scheduled job? —{' '}
            <span className="text-foreground font-medium">{jobName}</span>
          </>
        }
        footer={
          <>
            <Button size="sm" variant="outline" label="Cancel" onClick={closeModal} />
            <Button
              size="sm"
              variant="destructive"
              label="Delete"
              isLoading={isLoading}
              disabled={isPendingDelete}
              onClick={onDelete}
            />
          </>
        }
      />
    </>
  );
};

const itemsPerPage = 15;

type ScheduledJobsTableProps = {
  isInfluencer: boolean;
  compact?: boolean;
};

export default function ScheduledJobsTable(props: ScheduledJobsTableProps) {
  const { isInfluencer, compact = false } = props;
  const { workspaceId } = useWorkspace();
  const { openModal, modalRef, closeModal, isOpen } = useModal();
  const [pendingDeleteIds, setPendingDeleteIds] = useState<Set<string>>(() => new Set());

  const { currentPage, setCurrentPage, skip } = usePagination(itemsPerPage);

  const queryInput = useMemo(
    () => ({
      isInfluencer,
      skip,
      limit: itemsPerPage,
    }),
    [isInfluencer, skip],
  );

  const { data: allScannerJobsResponse } = api.scanner.getAllScannerJobs.useQuery(queryInput, {
    onError: serverError,
  });

  const rawJobs = allScannerJobsResponse?.scannerJobs ?? [];
  const scannerJobs = useMemo(
    () => rawJobs.filter((job) => !pendingDeleteIds.has(job.id)),
    [rawJobs, pendingDeleteIds],
  );
  const pendingStillInCache = rawJobs.filter((job) => pendingDeleteIds.has(job.id)).length;
  const totalCount = allScannerJobsResponse?.totalCount;
  const total = Math.max(0, (totalCount ?? 0) - pendingStillInCache);

  const searchParams = useSearchParams();
  const editingJobId = searchParams?.get('scannerJobId');

  let editingJob = scannerJobs?.find((j) => j.id === editingJobId);

  const { data: getEditingJobResponse } = api.scanner.getAllScannerJobs.useQuery(
    {
      isInfluencer,
      id: editingJobId!,
    },
    {
      onError: serverError,
      // only run this query if we have `scannerJobId` and unable to find the job from the job list query
      enabled: !!editingJobId && !editingJob && !!allScannerJobsResponse,
    },
  );

  editingJob = editingJob || getEditingJobResponse?.scannerJobs[0];

  useEffect(() => {
    editingJobId && editingJob && openModal();
  }, [editingJobId, editingJob]);

  const onPendingDelete = (jobId: string) => {
    setPendingDeleteIds((prev) => new Set(prev).add(jobId));
  };

  const onUndoDelete = (jobId: string) => {
    setPendingDeleteIds((prev) => {
      const next = new Set(prev);
      next.delete(jobId);
      return next;
    });
  };

  const onDeleteSettled = (jobId: string) => {
    setPendingDeleteIds((prev) => {
      const next = new Set(prev);
      next.delete(jobId);
      return next;
    });
  };

  const columns: Column<ScannerJob>[] = [
    {
      key: 'name',
      title: 'Job Name',
      render: (row) => (
        <div className="text-foreground truncate text-sm font-medium">{row.name}</div>
      ),
    },
    {
      title: 'Username',
      render: (row) => {
        const username = row.scannerSocialMedia[0]?.username;
        const type = row.scannerSocialMedia[0]?.type;
        return (
          <div className="min-w-0">
            <div className="text-foreground truncate text-sm font-medium">
              {username ? `@${username}` : '—'}
            </div>
            {type ? (
              <div className="text-muted-foreground truncate text-xs">{capitalCase(type)}</div>
            ) : null}
          </div>
        );
      },
    },
    ...(!compact
      ? [
          {
            title: 'Source',
            render: (row: ScannerJob) => {
              let source = 'Website';
              if (row.scannerSocialMedia[0]) {
                source = capitalCase(row.scannerSocialMedia[0].type);
              } else if (row.appId) {
                const appListing = Object.keys(ActorID).find(
                  (k) => ActorID[k as keyof typeof ActorID] === (row.apifyActorId as ActorID),
                );
                source = appListing ? capitalCase(appListing) : '—';
              }
              return <span className="text-muted-foreground text-sm">{source}</span>;
            },
          } as Column<ScannerJob>,
          {
            key: 'documentEvaluationTypeId',
            title: 'Policy Type',
            render: (row: ScannerJob) => (
              <span className="text-muted-foreground text-sm">
                {row.documentEvaluationType?.name || '—'}
              </span>
            ),
          } as Column<ScannerJob>,
        ]
      : []),
    {
      key: 'scheduler',
      title: 'Schedule',
      render: (row) => (
        <span className="text-muted-foreground text-sm">{formatSchedulerLabel(row.scheduler)}</span>
      ),
    },
    {
      title: 'Brand',
      render: (row) => (
        <span className="text-muted-foreground text-sm">{row.Brand?.name || UNKNOWN_BRAND_ID}</span>
      ),
    },
    ...(!compact
      ? [
          {
            key: 'createdAt',
            title: 'Created',
            tdClassName: 'text-muted-foreground whitespace-nowrap',
            render: (row: ScannerJob) => (
              <span className="text-muted-foreground text-sm tabular-nums">
                {dayjs(row.createdAt).format('MMM DD, YYYY')}
              </span>
            ),
          } as Column<ScannerJob>,
        ]
      : []),
    {
      title: 'Last Ran',
      tdClassName: 'text-muted-foreground whitespace-nowrap',
      render: (row) =>
        row.scannerRun.length ? (
          <span className="text-muted-foreground text-sm tabular-nums">
            {dayjs(row.scannerRun[0]?.createdAt).format('MMM DD, YYYY')}
          </span>
        ) : (
          <span className="text-muted-foreground/50 text-sm">—</span>
        ),
    },
    {
      key: 'id',
      title: 'Actions',
      render: (row) => {
        const editHref = isInfluencer
          ? `/${workspaceId}${Routes.SETTINGS}/influencer-monitoring?scannerJobId=${row.id}`
          : `/${workspaceId}${Routes.CONTENT_SCANNER}/jobs?jobId=${row.id}`;

        return (
          <ScheduledJobActions
            jobId={row.id}
            jobName={row.name}
            editHref={editHref}
            queryInput={queryInput}
            isPendingDelete={pendingDeleteIds.has(row.id)}
            onPendingDelete={onPendingDelete}
            onUndoDelete={onUndoDelete}
            onDeleteSettled={onDeleteSettled}
          />
        );
      },
    },
  ];

  return (
    <>
      <Table
        variant="plain"
        bleed={false}
        tableClassName={compact ? 'min-w-[640px]' : 'min-w-[1080px]'}
        columns={columns}
        data={scannerJobs}
      />
      {!allScannerJobsResponse ? null : !scannerJobs.length ? (
        <p className="text-muted-foreground border-border border-t px-4 py-8 text-center text-sm">
          No scheduled jobs yet
        </p>
      ) : null}
      {total !== scannerJobs.length && total > 1 && (
        <div className="border-border border-t px-4 py-3">
          <Pagination
            dense
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            total={Math.ceil(total / itemsPerPage)}
            itemsPerPage={itemsPerPage}
            totalItems={total}
            itemLabel="jobs"
            rangeOnly
          />
        </div>
      )}
      <NewJobButton
        isEdit={true}
        isInfluencer={isInfluencer}
        initialInput={{
          id: editingJob?.id ?? '',
          name: editingJob?.name ?? '',
          documentEvaluationTypeId: editingJob?.documentEvaluationTypeId,
          urls: editingJob?.urls,
          brandId: editingJob?.brandId ?? undefined,
          maxPages: editingJob?.maxPages ?? undefined,
          maxDepth: editingJob?.maxDepth ?? undefined,
          maxTrackedPages: editingJob?.maxTrackedPages ?? undefined,
          newPagesForSuccessiveScans: editingJob?.newPagesForSuccessiveScans ?? undefined,
          useSitemaps: editingJob?.useSitemaps ?? undefined,
          association: editingJob?.association as ScannerAssociation,
          archiveMode: editingJob?.archiveMode ?? undefined,
          ...(editingJob?.scannerSocialMedia.length && {
            socialMedia: {
              type: editingJob?.scannerSocialMedia[0]!.type,
              username: editingJob?.scannerSocialMedia[0]?.username ?? undefined,
            },
          }),
          appListing: editingJob?.appId
            ? {
                type:
                  (editingJob.apifyActorId as ActorID) === ActorID.GOOGLE_PLAY_STORE
                    ? AppListingType.GOOGLE_PLAY
                    : AppListingType.APP_STORE,
                appId: editingJob?.appId,
              }
            : undefined,
          autoCreateTask: editingJob?.autoCreateTask,
          autoCreateTaskProvider: editingJob?.autoCreateTaskProvider,
          autoCreateTaskUserId: editingJob?.autoCreateTaskUserId,
          autoCreateTaskThresholds: editingJob?.ActionThreshold,
          scheduler: editingJob?.scheduler ?? undefined,
          showAllFeedbackInDecisionStore: editingJob?.showAllFeedbackInDecisionStore,
          translationEnabled: editingJob?.translationEnabled,
          ...(editingJob?.scannerSocialMedia.length && {
            socialMediaId: editingJob?.scannerSocialMedia[0]?.id,
          }),
          attributeAssignments:
            editingJob?.AttributeAssignment.map((a) => ({
              id: a.id,
              attributeId: a.attributeId,
              value: a.attributeValue,
            })) || [],
        }}
        modalProperties={{ openModal, modalRef, closeModal, isOpen }}
      />
    </>
  );
}
